import type { FlowStep, SimulationSnapshot } from "../core/contracts";
import type { LiveOracleStubSource } from "../live/stub-source";
import type { SimulationEngine } from "../sim/engine";
import { CityViewController, browserFullscreenPort } from "./city-view";
import type { ExhibitOptions } from "../world/ore-exhibit";
import type { InteriorState } from "../world/city";
import {
  ORE_CASES,
  ORE_MEASUREMENTS,
  oreMembership,
  type OreScenario,
} from "../sim/ore";

const num = (n: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n);
const esc = (s: string) =>
  s.replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ]!,
  );
const phaseName = (s: FlowStep) =>
  ({
    network: "Connection",
    parse: "Parsing",
    "library-cache": "Cursor",
    transform: "Transformation",
    optimize: "Optimizer",
    execute: "Execution",
    io: "I/O",
    "buffer-cache": "Memory",
    return: "Results",
  })[s.phase];
const selectChapters = [
  ["Connection", 0],
  ["Cursor & mutex", 3],
  ["Transformation", 11],
  ["Execution plan", 13],
  ["I/O & memory", 17],
  ["Results", 27],
] as const;
const oreChapters = [
  ["Predicate tree", 0],
  ["Eligibility", 1],
  ["Two branches", 2],
  ["No double counting", 3],
  ["Follow the cost", 4],
  ["Choose a state", 5],
  ["Estimates vs rows", 6],
  ["Measured work", 7],
] as const;
const metricLabels = {
  employeeCalls: "EMPLOYEE_ID calls",
  starts: "Subquery starts",
  buffers: "Buffer gets",
  dateCalls: "CANONICAL_TO_DATE calls",
  wallMs: "Full-fetch median · ms",
} as const;

export class AppUI {
  private lastKey = "";
  private scenario: OreScenario = "original";
  private p: "TRUE" | "FALSE" | "UNKNOWN" = "TRUE";
  private q: "TRUE" | "FALSE" | "UNKNOWN" = "TRUE";
  private metric: keyof typeof metricLabels = "employeeCalls";
  private tourControl: ((guided: boolean) => void) | null = null;
  private cityViewControl: ((expanded: boolean) => void) | null = null;
  private readonly cityView: CityViewController;
  private expanded = false;
  private captionVisible = true;
  private guideScrollY = 0;
  private exhibitControl: ((options: ExhibitOptions) => void) | null = null;
  private interiorControl: ((mode: "inside" | "outside") => void) | null = null;

  constructor(
    private readonly root: HTMLElement,
    private readonly engine: SimulationEngine,
    private readonly liveSource: LiveOracleStubSource,
  ) {
    root.innerHTML = `<main class="app-shell">
      <header class="topbar"><a class="brand" href="./" aria-label="OraCity home"><span class="brand-mark"><i></i><i></i><i></i></span>ORA<span>CITY</span><small>THE DATABASE, EXPLAINED.</small></a><div class="edition"><i></i> INTERACTIVE FIELD GUIDE</div><a class="github-link" href="https://github.com/ora600pl/oracity" target="_blank" rel="noreferrer">GitHub ↗</a></header>
      <aside class="sidebar"><div class="rail-label">CHOOSE YOUR JOURNEY</div><div class="missions"><button data-mission="ore"><span>02</span><div>Inside the optimizer<small>The OR expansion investigation</small></div></button><button data-mission="select"><span>01</span><div>A SELECT through the city<small>From client to buffer cache</small></div></button></div><div class="rail-label chapter-label">STOPS ALONG THE WAY</div><nav data-chapters aria-label="Journey chapters"></nav><div class="map-wrap"><div class="rail-label">YOU ARE HERE</div><div data-map></div><p>One server process.<br>A city of shared structures.</p></div><button class="live-link" data-live>Live Oracle <span>Later ↗</span></button></aside>
      <section class="workspace"><div class="city-stage" role="region" aria-label="Oracle city overview"><canvas id="city-canvas"></canvas><div id="city-labels" aria-hidden="true"></div><div class="scene-shade"></div><div class="lesson-heading"><div class="overline"><span data-lesson-number></span><span data-build></span></div><h1 data-title></h1><p data-summary></p></div><div class="scene-foot"><span class="location"><i></i><span data-district></span></span><div class="camera-switch" role="group" aria-label="Camera"><button data-camera="journey" aria-pressed="true">Follow process</button><button data-camera="city" aria-pressed="false">Explore city</button></div></div></div>
      <div class="detail-grid"><section class="mechanism" aria-label="Mechanism cutaway"><header><span class="overline">UNDER THE SURFACE</span><span class="model-tag">EXPLANATORY CUTAWAY</span></header><div data-mechanism></div></section><aside class="reading-panel"><div class="overline">WHAT TO NOTICE</div><p class="takeaway" data-takeaway></p><div class="reading-rule"></div><div data-facts></div><details class="evidence"><summary>Evidence &amp; internals <span>↗</span></summary><span class="evidence-kind" data-evidence-kind></span><p data-evidence></p><div class="function-list" data-functions></div><a data-evidence-link target="_blank" rel="noreferrer">Read the lab notes ↗</a></details><div class="legend"><span><i></i>Server process</span><span><i class="legend-data"></i>SQL / data</span><span><i class="legend-wait"></i>Wait / rejection</span></div></aside></div></section>
      <footer class="transport"><div class="step-status"><span data-counter></span><strong data-play-state></strong></div><div class="playback"><button data-prev aria-label="Previous mechanism">←</button><button data-play class="primary">Pause</button><button data-next aria-label="Next mechanism">Next stop →</button><button data-replay aria-label="Replay this mechanism">↻</button></div><label class="auto-control"><input type="checkbox" data-auto> Auto tour</label><label class="pace-control">Pace <select data-speed aria-label="Tour pace"><option value="0.25">Museum</option><option value="0.5" selected>Slow</option><option value="1">Steady</option></select></label><div class="journey-progress"><i data-progress></i></div></footer>
      <dialog class="live-dialog"><button data-close aria-label="Close live mode information">×</button><div class="overline">LIVE ORACLE</div><h2>A future window into your database.</h2><p data-live-status></p><p>These journeys run entirely in your browser. Recorded lab cases explain observed behavior; timing and city geometry are illustrative.</p></dialog></main>`;
    const shell = root.querySelector<HTMLElement>(".app-shell")!;
    this.cityView = new CityViewController(browserFullscreenPort(shell));
    root.querySelector(".city-stage")!.insertAdjacentHTML(
      "beforeend",
      `
      <div class="city-view-controls" role="group" aria-label="City display">
        <button data-expand aria-pressed="false" aria-keyshortcuts="Escape" title="Expand the city without leaving this page">⤢ Expand city</button>
        <button data-caption aria-pressed="true" hidden>Hide caption</button>
        <button data-fullscreen aria-pressed="false" title="Show the city and playback controls in fullscreen">⛶ Fullscreen</button>
      </div><p class="city-view-notice" role="status" aria-live="polite" data-view-notice></p>
      <div class="interior-switch" role="group" aria-label="Building view" hidden><button data-interior="inside" aria-pressed="false">↳ Enter building</button><button data-interior="outside" aria-pressed="true">City exterior</button></div>
      <div class="interior-heading"><small data-room></small><strong data-room-lesson></strong><small>OPEN ARCHITECTURAL CUTAWAY</small></div>
      <div class="interior-console"><p class="interior-focus" data-interior-focus role="status" aria-live="polite"></p><div data-interior-options></div><p class="interior-boundary" data-interior-boundary></p></div>`,
    );
    this.cityView.subscribe((state) => {
      if (state.expanded !== this.expanded) {
        if (state.expanded) this.guideScrollY = window.scrollY;
        this.expanded = state.expanded;
        shell.classList.toggle("city-expanded", state.expanded);
        document.body.classList.toggle("city-view-open", state.expanded);
        this.cityViewControl?.(state.expanded);
        if (!state.expanded)
          window.scrollTo({ top: this.guideScrollY, behavior: "instant" });
        root
          .querySelector<HTMLButtonElement>("[data-expand]")!
          .focus({ preventScroll: true });
      }
      const expand = root.querySelector<HTMLButtonElement>("[data-expand]")!;
      expand.textContent = state.expanded ? "↙ Field guide" : "⤢ Expand city";
      expand.setAttribute("aria-pressed", String(state.expanded));
      expand.title = state.expanded
        ? "Return to mechanisms and notes (Esc)"
        : "Expand the city without leaving this page";
      expand.disabled = state.busy;
      const full = root.querySelector<HTMLButtonElement>("[data-fullscreen]")!;
      full.textContent = state.fullscreen
        ? "⛶ Exit fullscreen"
        : "⛶ Fullscreen";
      full.setAttribute("aria-pressed", String(state.fullscreen));
      full.disabled = state.busy;
      root.querySelector<HTMLButtonElement>("[data-caption]")!.hidden =
        !state.expanded;
      this.text("[data-view-notice]", state.notice);
    });
    this.bind();
  }

  bindCityViewControls(handler: (expanded: boolean) => void): void {
    this.cityViewControl = handler;
    handler(this.expanded);
  }

  bindTourControls(handler: (guided: boolean) => void): void {
    this.tourControl = handler;
    handler(true);
  }

  bindExhibitControls(
    options: (options: ExhibitOptions) => void,
    mode: (mode: "inside" | "outside") => void,
  ): void {
    this.exhibitControl = options;
    this.interiorControl = mode;
    this.exhibitControl(this.exhibitOptions());
  }

  showInterior(state: InteriorState): void {
    const stage = this.root.querySelector<HTMLElement>(".city-stage")!;
    stage.classList.toggle("has-interior", state.available);
    stage.classList.toggle("interior-active", state.active);
    this.root.querySelector<HTMLElement>(".interior-switch")!.hidden =
      !state.available;
    this.root
      .querySelector("[data-interior='inside']")!
      .setAttribute("aria-pressed", String(state.active));
    this.root
      .querySelector("[data-interior='outside']")!
      .setAttribute("aria-pressed", String(!state.active));
    this.text("[data-room]", state.room);
    this.text("[data-interior-boundary]", state.boundary);
    stage.setAttribute(
      "aria-label",
      state.active ? state.room : "Oracle city overview",
    );
  }

  showInteriorFocus(text: string): void {
    this.text("[data-interior-focus]", text);
  }

  private exhibitOptions(): ExhibitOptions {
    return {
      scenario: this.scenario,
      p: this.p,
      q: this.q,
      metric: this.metric,
    };
  }

  private renderInteriorOptions(step: FlowStep): void {
    const panel = this.root.querySelector("[data-interior-options]")!;
    const select = (
      attribute: string,
      label: string,
      value: string,
      entries: [string, string][],
    ) =>
      `<label>${label}<select ${attribute} aria-label="${label} in building">${entries.map(([key, text]) => `<option value="${key}" ${key === value ? "selected" : ""}>${text}</option>`).join("")}</select></label>`;
    const truths: [string, string][] = ["TRUE", "FALSE", "UNKNOWN"].map((v) => [
      v,
      v,
    ]);
    panel.innerHTML = ["ore-cost", "ore-decision", "ore-estimates"].includes(
      step.id,
    )
      ? select(
          "data-room-scenario",
          "Recorded case",
          this.scenario,
          Object.entries(ORE_CASES).map(([key, c]) => [key, c.label]),
        )
      : step.id === "ore-guard"
        ? select('data-room-truth="p"', "Predicate P", this.p, truths) +
          select('data-room-truth="q"', "Predicate Q", this.q, truths)
        : step.id === "ore-runtime"
          ? select(
              "data-room-metric",
              "Measurement",
              this.metric,
              Object.entries(metricLabels),
            )
          : "";
  }

  private bind(): void {
    this.root.addEventListener("click", (event) => {
      const b = (event.target as HTMLElement).closest<HTMLButtonElement>(
        "button",
      );
      if (!b) return;
      if (b.hasAttribute("data-expand")) void this.cityView.toggleExpanded();
      if (b.hasAttribute("data-fullscreen"))
        void this.cityView.toggleFullscreen();
      if (b.hasAttribute("data-caption")) {
        this.captionVisible = !this.captionVisible;
        this.root
          .querySelector(".app-shell")!
          .classList.toggle("caption-hidden", !this.captionVisible);
        b.textContent = this.captionVisible ? "Hide caption" : "Show caption";
        b.setAttribute("aria-pressed", String(this.captionVisible));
      }
      const s = this.engine.snapshot();
      if (b.dataset.mission) {
        this.lastKey = "";
        this.engine.run(b.dataset.mission as "ore" | "select");
      }
      if (b.dataset.stop) {
        this.engine.seek(Number(b.dataset.stop));
        this.root
          .querySelector<HTMLButtonElement>(`[data-stop="${b.dataset.stop}"]`)
          ?.focus({ preventScroll: true });
      }
      if (b.hasAttribute("data-prev")) this.engine.seek(s.activeStepIndex - 1);
      if (b.hasAttribute("data-next")) this.engine.seek(s.activeStepIndex + 1);
      if (b.hasAttribute("data-replay")) this.engine.seek(s.activeStepIndex);
      if (b.hasAttribute("data-play")) {
        if (!s.running || s.stepProgress >= 1)
          this.engine.seek(s.activeStepIndex);
        else this.engine.togglePause();
      }
      if (b.dataset.camera) {
        this.tourControl?.(b.dataset.camera === "journey");
        this.root
          .querySelectorAll<HTMLButtonElement>("[data-camera]")
          .forEach((el) =>
            el.setAttribute(
              "aria-pressed",
              String(el.dataset.camera === b.dataset.camera),
            ),
          );
      }
      if (b.dataset.interior) {
        if (b.dataset.interior === "inside") {
          this.tourControl?.(true);
          this.root
            .querySelectorAll<HTMLButtonElement>("[data-camera]")
            .forEach((el) =>
              el.setAttribute(
                "aria-pressed",
                String(el.dataset.camera === "journey"),
              ),
            );
        }
        this.interiorControl?.(b.dataset.interior as "inside" | "outside");
      }
      if (b.dataset.scenario) {
        this.scenario = b.dataset.scenario as OreScenario;
        this.renderMechanism(this.engine.snapshot());
        this.render(this.engine.snapshot());
        this.root
          .querySelector<HTMLButtonElement>(
            `[data-scenario="${this.scenario}"]`,
          )
          ?.focus({ preventScroll: true });
      }
      if (b.hasAttribute("data-live")) {
        this.text("[data-live-status]", this.liveSource.status().detail);
        this.root.querySelector<HTMLDialogElement>("dialog")?.showModal();
      }
      if (b.hasAttribute("data-close"))
        this.root.querySelector<HTMLDialogElement>("dialog")?.close();
    });
    this.root.addEventListener("change", (event) => {
      const input = event.target as HTMLInputElement;
      if (input.hasAttribute("data-auto"))
        this.engine.setAutoAdvance(input.checked);
      if (input.hasAttribute("data-speed"))
        this.engine.setSpeed(Number(input.value));
      if (input.hasAttribute("data-room-scenario"))
        this.scenario = input.value as OreScenario;
      if (input.dataset.roomTruth === "p")
        this.p = input.value as typeof this.p;
      if (input.dataset.roomTruth === "q")
        this.q = input.value as typeof this.q;
      if (input.hasAttribute("data-room-metric"))
        this.metric = input.value as typeof this.metric;
      if (
        input.hasAttribute("data-room-scenario") ||
        input.dataset.roomTruth ||
        input.hasAttribute("data-room-metric")
      ) {
        const selector = input.dataset.roomTruth
          ? `[data-room-truth="${input.dataset.roomTruth}"]`
          : input.hasAttribute("data-room-scenario")
            ? "[data-room-scenario]"
            : "[data-room-metric]";
        this.renderMechanism(this.engine.snapshot());
        this.root
          .querySelector<HTMLSelectElement>(selector)
          ?.focus({ preventScroll: true });
      }
      if (input.dataset.truth === "p") this.p = input.value as typeof this.p;
      if (input.dataset.truth === "q") this.q = input.value as typeof this.q;
      if (input.hasAttribute("data-metric"))
        this.metric = input.value as typeof this.metric;
      if (input.dataset.truth || input.hasAttribute("data-metric")) {
        this.renderMechanism(this.engine.snapshot());
        const selector = input.dataset.truth
          ? `[data-truth="${input.dataset.truth}"]`
          : "[data-metric]";
        this.root
          .querySelector<HTMLSelectElement>(selector)
          ?.focus({ preventScroll: true });
      }
    });
    // Escape must also work after OrbitControls/browser fullscreen move focus to body.
    document.addEventListener("keydown", (event) => {
      if (
        event.key === "Escape" &&
        this.expanded &&
        !this.cityView.snapshot().fullscreen
      ) {
        event.preventDefault();
        void this.cityView.toggleExpanded();
      }
    });
    this.root.addEventListener("keydown", (event) => {
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLSelectElement ||
        this.root.querySelector("dialog[open]")
      )
        return;
      if (event.key === "ArrowRight") {
        event.preventDefault();
        this.engine.seek(this.engine.snapshot().activeStepIndex + 1);
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.engine.seek(this.engine.snapshot().activeStepIndex - 1);
      }
    });
  }

  render(s: SimulationSnapshot): void {
    const step = s.steps[Math.max(0, s.activeStepIndex)]!;
    const ore = s.flow === "ore";
    const key = `${s.flow}:${s.activeStepIndex}`;
    if (key !== this.lastKey) {
      this.lastKey = key;
      this.text("[data-title]", step.label);
      this.text("[data-room-lesson]", step.label);
      this.text("[data-summary]", step.summary);
      this.text(
        "[data-build]",
        ore ? "LAB · ORACLE 19.32" : "LAB · ORACLE 19.25",
      );
      this.text(
        "[data-lesson-number]",
        `${ore ? "JOURNEY 02" : "JOURNEY 01"} / ${phaseName(step).toUpperCase()}`,
      );
      this.text(".location > span", `${phaseName(step)} district`);
      this.text("[data-evidence]", step.evidence.note);
      this.text("[data-evidence-kind]", step.evidence.label);
      this.root.querySelector("[data-functions]")!.innerHTML =
        step.functionChain.map((f) => `<code>${esc(f)}</code>`).join("");
      this.root.querySelector<HTMLAnchorElement>("[data-evidence-link]")!.href =
        `https://github.com/ora600pl/oracity/blob/main/docs/${ore ? "ORE" : "EVIDENCE"}.md`;
      const chapters = ore ? oreChapters : selectChapters;
      const current = chapters.reduce(
        (last, [, start], index) => (s.activeStepIndex >= start ? index : last),
        0,
      );
      this.root.querySelector("[data-chapters]")!.innerHTML = chapters
        .map(
          ([name, start], i) =>
            `<button data-stop="${start}" ${i === current ? 'aria-current="step"' : ""}><span>${String(i + 1).padStart(2, "0")}</span>${name}</button>`,
        )
        .join("");
      this.root
        .querySelectorAll<HTMLButtonElement>("[data-mission]")
        .forEach((b) =>
          b.setAttribute("aria-pressed", String(b.dataset.mission === s.flow)),
        );
      this.renderMap(step);
      this.renderMechanism(s);
    }
    this.text(
      "[data-counter]",
      `${String(s.activeStepIndex + 1).padStart(2, "0")} / ${s.steps.length}`,
    );
    this.text(
      "[data-play-state]",
      !s.running
        ? "Journey complete"
        : s.paused
          ? s.stepProgress >= 1
            ? "Take your time"
            : "Paused"
          : "Following the process",
    );
    this.text(
      "[data-play]",
      !s.running || s.stepProgress >= 1
        ? "Replay"
        : s.paused
          ? "Resume"
          : "Pause",
    );
    this.root.querySelector<HTMLButtonElement>("[data-prev]")!.disabled =
      s.activeStepIndex <= 0;
    this.root.querySelector<HTMLButtonElement>("[data-next]")!.disabled =
      s.activeStepIndex >= s.steps.length - 1;
    this.root.querySelector<HTMLElement>("[data-progress]")!.style.width =
      `${((s.activeStepIndex + s.stepProgress) / s.steps.length) * 100}%`;
    this.root
      .querySelector<HTMLElement>(".mechanism")!
      .style.setProperty("--step-progress", String(s.stepProgress));
    this.root
      .querySelector(".app-shell")!
      .classList.toggle("paused", s.paused || !s.running);
    this.root
      .querySelector("[data-nested-cost]")
      ?.classList.toggle(
        "visited",
        this.scenario === "fixed" && s.stepProgress > 0.4,
      );
    if (step.id === "ore-predicate" || step.id === "ore-cost") {
      const selectors =
        step.id === "ore-cost" && this.scenario !== "fixed"
          ? [".sql-token", ".and-label"]
          : [".sql-token", ".predicate.own", ".and-label", ".subquery"];
      this.root
        .querySelectorAll(".probe-active")
        .forEach((el) => el.classList.remove("probe-active"));
      const index = Math.min(
        selectors.length - 1,
        Math.floor(s.stepProgress * selectors.length),
      );
      this.root.querySelector(selectors[index]!)?.classList.add("probe-active");
    }
  }

  private text(selector: string, text: string): void {
    const el = this.root.querySelector(selector);
    if (el && el.textContent !== text) el.textContent = text;
  }

  private renderMap(step: FlowStep): void {
    const points = [
      ["client", 20, 99],
      ["parser", 47, 74],
      ["library-cache", 79, 74],
      ["transformer", 113, 74],
      ["optimizer", 147, 74],
      ["plan", 174, 47],
      ["table", 147, 22],
      ["buffer-cache", 80, 22],
    ] as const;
    let loc = step.district;
    if (loc === "server") loc = "parser";
    if (loc === "mutex-gate") loc = "library-cache";
    if (["io-queue", "storage", "index", "join"].includes(loc)) loc = "table";
    if (loc === "hash-chains") loc = "buffer-cache";
    this.root.querySelector("[data-map]")!.innerHTML =
      `<svg viewBox="0 0 194 120" role="img" aria-label="City route: ${phaseName(step)}"><path class="map-grid" d="M8 12H188 M8 44H188 M8 76H188 M8 108H188 M20 8V114 M52 8V114 M84 8V114 M116 8V114 M148 8V114 M180 8V114"/><path class="map-route" d="M20 99L47 74H147L174 47L147 22H80"/>${points.map(([id, x, y]) => `<rect x="${x - 4}" y="${y - 4}" width="8" height="8" rx="2" class="${id === loc ? "map-active" : "map-stop"}"/>${id === loc ? `<circle cx="${x}" cy="${y}" r="10" class="map-ring"/>` : ""}`).join("")}</svg>`;
  }

  private renderMechanism(s: SimulationSnapshot): void {
    const step = s.steps[Math.max(0, s.activeStepIndex)]!,
      ore = s.flow === "ore";
    this.root.querySelector("[data-mechanism]")!.innerHTML = ore
      ? this.oreView(step)
      : this.selectView(step, s);
    this.renderInteriorOptions(step);
    this.exhibitControl?.(this.exhibitOptions());
    const notes: Record<string, string> = {
      "ore-predicate":
        "The expensive subquery is nested inside AND, itself underneath OR. That position matters to the cost traversal.",
      "ore-validity":
        "Considering a transformation is not evidence that it was selected. Follow the candidate all the way to the decision.",
      "ore-branches":
        "OR expansion can help even when one branch keeps a full scan. Two branches do not imply two server processes.",
      "ore-guard":
        "UNION ALL does not remove duplicates. Branch predicates prevent the same source row from qualifying twice.",
      "ore-cost":
        this.scenario === "fixed"
          ? "The nested subquery cost is now included on both sides. The SQL and data did not change."
          : this.scenario === "histograms"
            ? "Better estimates reduce the candidate cost. They do not fill in the missing term on the other side."
            : "The original candidate appears cheap because its nested subquery cost is omitted in this observed path.",
      "ore-decision":
        ORE_CASES[this.scenario].winner === "ore"
          ? "ORE wins by about 1.70% in estimated cost here. That percentage is not a predicted runtime speedup."
          : "The expanded candidate was valid and costed, but lost the comparison. A rejected candidate is not a runtime operation.",
      "ore-estimates":
        "These are rows in a diagnostic filter. Subquery starts and final result rows are different measurements.",
      "ore-runtime":
        "92.47% fewer EMPLOYEE_ID calls; essentially the same buffer gets. The measured benefit is CPU work, not less disk traffic.",
    };
    this.text("[data-takeaway]", notes[step.id] ?? this.selectTakeaway(step));
    this.root.querySelector("[data-facts]")!.innerHTML = ore
      ? '<div class="fact"><span>Evidence profile</span><b>19.32 · AArch64</b></div><div class="fact"><span>Recorded</span><b>08 September 2026</b></div><div class="fact"><span>Playback</span><b>Illustrative timing</b></div>'
      : Object.entries(step.facts ?? {})
          .map(
            ([k, v]) =>
              `<div class="fact"><span>${esc(k)}</span><b>${esc(v)}</b></div>`,
          )
          .join("");
  }

  private scenarioPicker(): string {
    return `<div class="scenario-picker" role="group" aria-label="Recorded optimizer scenario">${Object.entries(
      ORE_CASES,
    )
      .map(
        ([id, c]) =>
          `<button data-scenario="${id}" aria-pressed="${id === this.scenario}">${c.label}</button>`,
      )
      .join(
        "",
      )}</div><p class="case-caption">${ORE_CASES[this.scenario].detail} · recorded case</p>`;
  }
  private predicateTree(cost = false): string {
    return `<div class="predicate-tree"><div class="tree-root"><span class="sql-token">OR</span><small>Query block</small></div><div class="tree-branches"><div class="predicate own"><span class="node-label">P · OWN ASSIGNMENTS</span><code>person_id = employee_id()</code><span class="node-dot"></span></div><div class="predicate delegated"><span class="node-label">Q · DELEGATION</span><div class="and-label">AND <span>all conditions must hold</span></div><div class="filter-nodes"><span>Valid dates</span><span>Type E / C</span><span>Business group</span><span class="subquery" data-nested-cost><b>Correlated subquery</b>${cost ? (this.scenario === "fixed" ? "↳ Cost included" : "↳ Cost omitted in base") : "Nested one level deeper"}</span></div></div></div></div>`;
  }
  private oreView(step: FlowStep): string {
    if (step.id === "ore-predicate")
      return `<h2>The shape of the question</h2>${this.predicateTree()}<p class="diagram-caption">Explanatory SQL shape · boxes are predicates, not data blocks.</p>`;
    if (step.id === "ore-validity")
      return `<h2>A candidate earns a place at the table</h2><div class="gate-sequence"><div class="gate-card accepted"><span>01 / ELIGIBILITY</span><b>Can expand</b><p>The recorded query passes the validity checks.</p><i>✓</i></div><div class="flow-arrow">→</div><div class="gate-card"><span>02 / CANDIDATE</span><b>Build a variant</b><p>Copy the query block and generate branches.</p><i>◇</i></div><div class="flow-arrow">→</div><div class="gate-card"><span>03 / COST</span><b>Still undecided</b><p>Compare with the current best state.</p><i>?</i></div></div><div class="lesson-note">Valid → generated → costed → selected or rejected</div>`;
    if (step.id === "ore-branches")
      return `<h2>Two access paths, one statement</h2><div class="branch-grid"><div class="branch-card"><span class="node-label">BRANCH 1 · P</span><h3>Own assignments</h3><div class="operator index-op">INDEX RANGE SCAN</div><div class="connector">↓</div><div class="operator">TABLE ACCESS BY ROWID</div></div><div class="branch-card"><span class="node-label">BRANCH 2 · Q AND LNNVL(P)</span><h3>Delegated assignments</h3><div class="operator">TABLE ACCESS FULL</div><div class="connector">↓</div><div class="operator">Filters + correlated subquery</div></div></div><div class="union-node">↘ <b>UNION ALL</b> ↙ <span>One result stream</span></div>`;
    if (step.id === "ore-guard") {
      const m = oreMembership(this.p, this.q);
      const select = (id: string, v: string) =>
        `<select data-truth="${id}" aria-label="Predicate ${id.toUpperCase()}">${["TRUE", "FALSE", "UNKNOWN"].map((x) => `<option ${x === v ? "selected" : ""}>${x}</option>`).join("")}</select>`;
      return `<h2>Try a row that matches both branches</h2><div class="truth-inputs"><label>P · Own assignment ${select("p", this.p)}</label><label>Q · Delegation ${select("q", this.q)}</label></div><div class="branch-grid"><div class="branch-card ${m.first ? "selected-branch" : ""}"><span class="node-label">BRANCH 1</span><h3>P</h3><p>${m.first ? "✓ Row passes" : "— Row does not pass"}</p></div><div class="branch-card ${m.second ? "selected-branch" : ""}"><span class="node-label">BRANCH 2</span><h3>Q AND LNNVL(P)</h3><p>${m.second ? "✓ Row passes" : "— Row does not pass"}</p></div></div><div class="lesson-note"><strong>${Number(m.first) + Number(m.second)} ${m.original ? "copy" : "copies"} of this source row</strong><span>Original P OR Q: ${m.original ? "qualifies" : "does not qualify"} · UNKNOWN is not TRUE.</span></div>`;
    }
    if (step.id === "ore-cost")
      return `<h2>Where the missing cost lives</h2>${this.scenarioPicker()}${this.predicateTree(true)}`;
    if (step.id === "ore-decision") {
      const c = ORE_CASES[this.scenario];
      return `<h2>The first-pass decision</h2>${this.scenarioPicker()}<div class="cost-comparison"><div class="cost-card ${c.winner === "base" ? "winner" : ""}"><span>ORIGINAL QUERY</span><strong>${num(c.base)}</strong><div class="cost-track"><i style="width:${(c.base / 550000) * 100}%"></i></div><b>${c.winner === "base" ? "✓ Best state" : "Higher estimated cost"}</b></div><div class="cost-card ${c.winner === "ore" ? "winner" : ""}"><span>EXPANDED CANDIDATE</span><strong>${num(c.ore)}</strong><div class="cost-track"><i style="width:${(c.ore / 550000) * 100}%"></i></div><b>${c.winner === "ore" ? "✓ Best state" : "Rejected on cost"}</b></div></div><p class="diagram-caption">Cost units, not milliseconds · same optimization stage · shared 550,000-unit scale.</p>`;
    }
    if (step.id === "ore-estimates") {
      const c = ORE_CASES[this.scenario];
      return `<h2>How many rows reach the filter?</h2>${this.scenarioPicker()}${this.bar("CBO estimate", c.estimatedRows, 278273, "blue")}${this.bar("Measured rows", c.actualRows, 278273, "yellow")}<div class="lesson-note"><strong>${(c.estimatedRows / c.actualRows).toFixed(2)}× estimated / measured</strong><span>Diagnostic aggregate · identical underlying data.</span></div>`;
    }
    const a = ORE_MEASUREMENTS.base[this.metric],
      b = ORE_MEASUREMENTS.ore[this.metric];
    const fmt = (n: number) =>
      this.metric === "wallMs" ? n.toFixed(2) : num(n);
    return `<h2>What changed when the plan ran?</h2><div class="runtime-head"><label>Measurement <select data-metric aria-label="Runtime measurement">${Object.entries(
      metricLabels,
    )
      .map(
        ([id, label]) =>
          `<option value="${id}" ${id === this.metric ? "selected" : ""}>${label}</option>`,
      )
      .join(
        "",
      )}</select></label><span class="measured-tag">RECORDED A/B</span></div>${this.bar("Without ORE", a, Math.max(a, b), "blue", fmt(a))}${this.bar("With ORE", b, Math.max(a, b), "yellow", fmt(b))}<div class="result-seal"><span>✓</span><div><b>220 result rows. Same multiset.</b><small>Measured lab runs · both plans fully fetched</small></div></div>`;
  }
  private bar(
    label: string,
    value: number,
    max: number,
    color: string,
    display = num(value),
  ): string {
    return `<div class="measure-row"><div><span>${label}</span><strong>${display}</strong></div><div class="measure-track"><i class="${color}" style="width:${(value / max) * 100}%"></i></div></div>`;
  }
  private selectTakeaway(step: FlowStep): string {
    if (step.phase === "library-cache")
      return "A parent identifies SQL text; executable children belong to their compilation environments. Follow the same server process. Gate timing is illustrative.";
    if (["io", "buffer-cache"].includes(step.phase))
      return "Block identity persists from the file request into memory. A cache hit would avoid the physical read; this journey illustrates a miss.";
    if (["transform", "optimize"].includes(step.phase))
      return "Transformations and costing interact. Open Journey 02 for the ORE investigation and a candidate-by-candidate explanation.";
    return "Read the cutaway while the city gives you location. Each step stops automatically; continue when the mechanism makes sense.";
  }
  private selectView(step: FlowStep, s: SimulationSnapshot): string {
    if (step.phase === "library-cache") {
      const i = s.activeStepIndex,
        scan = step.id === "child-zero-scan",
        reject = i >= 8,
        created = i >= 9,
        held = i >= 6 && i < 10;
      return `<h2>Inside the library cache</h2><div class="lock-ledger"><span class="process-chip">▰ SERVER 1</span><span class="lock-chip ${i === 4 ? "held" : ""}">Bucket mutex · ${i === 4 ? "held" : "released"}</span><span class="lock-chip ${held ? "held" : ""}">Parent mutex · ${held ? "X / SERVER 1" : "not held"}</span></div><div class="parent-handle">PARENT SQL HANDLE <span>${i >= 5 ? "Found" : "Lookup"} · child list ↓</span></div><div class="branch-grid cursor-rack"><div class="branch-card ${reject ? "rejected" : scan ? "scanning" : ""}"><span class="node-label">CHILD 0</span><h3>${reject ? "Environment mismatch" : scan ? "Inspecting compatibility" : "Existing executable"}</h3><p>Optimizer environment<br><code>3070863379</code></p><div class="scan-line"><i></i></div></div><div class="branch-card ${created ? "selected-branch" : "dormant"}"><span class="node-label">CHILD 1</span><h3>${created ? "New child allocated" : "Not allocated"}</h3><p>Requested environment<br><code>1902962930</code></p></div></div><p class="diagram-caption">No waiters in this scenario. Contention would enter kgxWait. Child IDs are from a separate sharing experiment; gate timing is modeled.</p>`;
    }
    if (["io", "buffer-cache"].includes(step.phase)) {
      const multi = ["scattered-read", "multi-block-flight"].includes(step.id),
        hash = ["buffer-hash", "cbc-latch", "buffer-link"].includes(step.id);
      return `<h2>${hash ? "Block identity → hash bucket → buffer header" : multi ? "A multiblock request reaches memory" : "A single-block request reaches memory"}</h2><div class="io-route"><div class="io-station"><span class="node-label">DATAFILE 1</span><div class="block-container">${Array.from({ length: multi ? 4 : 1 }, (_, i) => `<span class="data-block">${multi ? 114393 + i : 114385}</span>`).join("")}</div></div><div class="io-line"><span>${hash ? "Lookup" : "Read completion"}</span><i></i></div><div class="io-station"><span class="node-label">${hash ? "BUFFER HASH CHAIN" : "BUFFER CACHE"}</span><div class="buffer-slots">${Array.from({ length: 8 }, (_, i) => `<i class="${i < (multi ? 4 : 1) ? "filled" : ""}"></i>`).join("")}</div></div></div>${hash ? '<div class="hash-cutaway"><code>(file#, block#)</code><span>→</span><code>kcb_hash_num</code><span>→</span><b>bucket</b><span>→</span><b>BH → BH → BH</b></div>' : `<div class="lesson-note"><strong>${multi ? "db file scattered read" : "db file sequential read"}</strong><span>${multi ? "4 blocks · individual destination buffers" : "1 block · index / ROWID path"}</span></div>`}<p class="diagram-caption">${hash ? "CBC latch protects chain operations briefly; the bucket layout is illustrative." : "Recorded block identities with modeled travel and queue timing. Full scans can use other I/O paths."}</p>`;
    }
    if (
      ["transform", "optimize"].includes(step.phase) &&
      step.action !== "plan-node"
    )
      return '<h2>A conversation between transformation and costing</h2><div class="gate-sequence"><div class="gate-card"><span>QUERY BLOCK</span><b>Build a candidate</b><p>Consider equivalent forms.</p></div><div class="flow-arrow">⇄</div><div class="gate-card accepted"><span>CBO</span><b>Estimate its work</b><p>Keep the best state so far.</p></div></div><button class="ore-invitation" data-mission="ore">Explore the OR expansion investigation <span>↗</span></button>';
    if (step.phase === "execute" || step.action === "plan-node")
      return '<h2>The row-source tree</h2><div class="plan-root">SELECT → SORT ORDER BY → HASH GROUP BY</div><div class="union-node"><b>HASH JOIN</b><span>Customers build · orders probe</span></div><div class="branch-grid"><div class="branch-card"><span class="node-label">CUSTOMERS</span><div class="operator index-op">INDEX RANGE SCAN</div><div class="connector">↓</div><div class="operator">TABLE ACCESS BY ROWID</div></div><div class="branch-card"><span class="node-label">ORDERS</span><div class="operator">TABLE ACCESS FULL</div><div class="connector">↓</div><div class="operator">Filter STATUS = OPEN</div></div></div><p class="diagram-caption">Controlled SELECT · plan hash 3896265596 · final cost 163</p>';
    const returning = step.phase === "return";
    return `<h2>${returning ? "The answer returns to its client" : "One process carries the request"}</h2><div class="request-route"><div class="endpoint"><span class="node-label">SQL CLIENT</span><div class="document-shape">${returning ? "12 rows" : "SELECT"}<i></i><i></i></div></div><div class="request-wire"><span>Oracle Net ${returning ? "←" : "→"}</span><i></i></div><div class="endpoint"><span class="node-label">DEDICATED SERVER</span><div class="server-vehicle">▰ <b>SERVER 1</b></div></div></div><div class="lesson-note"><strong>${returning ? "12 grouped results" : "Parse · locate a cursor · execute · fetch"}</strong><span>${returning ? "One completed controlled SELECT." : "Buildings represent mechanisms and structures visited by this process."}</span></div>`;
  }
}
